import type { CSSProperties } from "react";
import { useTheme } from "../../hooks/useTheme.ts";
import type { ThemePack } from "../../providers/themeProvider.tsx";
import { getConfig } from "../../api/client.ts";
import logoIsLight from "../../../assets/logo.svg";
import logoIsDark from "../../../assets/logo-dark.svg";
import logoMaikozLight from "../../../assets/logo-maikoz.svg";
import logoMaikozDark from "../../../assets/logo-maikoz-dark.svg";
import logoCtlpLight from "../../../assets/logo-ctlp.svg";
import logoCtlpDark from "../../../assets/logo-ctlp-dark.svg";
import markIs from "../../../assets/mark-instantsystems.svg";
import markMaikoz from "../../../assets/mark-maikoz.svg";
import markCtlp from "../../../assets/mark-ctlp.svg";

interface PackAssets { light: string; dark: string; mark: string; alt: string }

const PACK_LOGOS: Record<ThemePack, PackAssets> = {
  instantsystems: { light: logoIsLight, dark: logoIsDark, mark: markIs, alt: "Instant Systems" },
  maikoz:         { light: logoMaikozLight, dark: logoMaikozDark, mark: markMaikoz, alt: "Maikoz" },
  ctlp:           { light: logoCtlpLight, dark: logoCtlpDark, mark: markCtlp, alt: "Cantaloupe" },
};

type LogoVariant = "light" | "dark" | "mark";

/** Per-host logo override from `getConfig().Logo`. Empty strings and
 *  unsubstituted placeholders fall through to the pack default. */
function resolveLogoSrc(pack: ThemePack, variant: LogoVariant): string {
  try {
    const override = getConfig().Logo?.[variant];
    if (override && override !== "" && !override.startsWith("__")) return override;
  } catch { /* config not loaded yet */ }
  return PACK_LOGOS[pack][variant];
}

export function Logo({ className, alt, style }: { className?: string; alt?: string; style?: CSSProperties }) {
  const { resolvedTheme, themePack } = useTheme();
  const pack = PACK_LOGOS[themePack];
  const src = resolveLogoSrc(themePack, resolvedTheme === "dark" ? "dark" : "light");
  return <img src={src} alt={alt ?? pack.alt} className={className} style={style} />;
}

/** Square brand mark — used for the topbar home badge and favicon. Pack-aware,
 *  not theme-aware (a single mark works on light + dark surfaces). */
export function BrandMark({ className, alt, style }: { className?: string; alt?: string; style?: CSSProperties }) {
  const { themePack } = useTheme();
  const pack = PACK_LOGOS[themePack];
  const src = resolveLogoSrc(themePack, "mark");
  return <img src={src} alt={alt ?? pack.alt} className={className} style={style} />;
}

/** Resolve the mark URL for a given pack — used by ThemeProvider to swap the
 *  document favicon. Defined here so all pack-asset wiring stays in one place. */
export function getMarkUrl(pack: ThemePack): string {
  return resolveLogoSrc(pack, "mark");
}
